import React, { useState, useEffect } from 'react'
import { getCompressorSlaves, getCompressorAnalytics } from '../../auth/compressor/CompressorAnalyticsApi';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Chip,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    CircularProgress,
    Alert,
    Snackbar,
    FormControlLabel
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Updated parameter options for Connectivity analytics
const parameterOptions = [
    { value: "connectivity_status", label: "Connectivity History" }
];

const CompressorAnalytics = ({ onSidebarToggle, sidebarVisible }) => {
    // State for filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDevice, setFilterDevice] = useState('');
    const [filterStartDate, setFilterStartDate] = useState(dayjs().subtract(1, 'day'));
    const [filterEndDate, setFilterEndDate] = useState(dayjs());
    const [searchClicked, setSearchClicked] = useState(false);
    const [devices, setDevices] = useState(['all']);
    const [deviceObjects, setDeviceObjects] = useState([]);
    const [selectedParameter, setSelectedParameter] = useState(['connectivity_status']);
    const [selectedParameter2, setSelectedParameter2] = useState(['connectivity_status']);
    const [selectedParameter3, setSelectedParameter3] = useState(['connectivity_status']);
    const [filterDevice2, setFilterDevice2] = useState('all');
    const [filterDevice3, setFilterDevice3] = useState('all');
    const [openStart, setOpenStart] = useState(false);
    const [openEnd, setOpenEnd] = useState(false);

    // Loading and error states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [compareLoading, setCompareLoading] = useState(false);
    const [compareLoading2, setCompareLoading2] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // State for merging comparison into main chart
    const [mergeComparison, setMergeComparison] = useState(false);

    const styles = {
        mainContent: {
            width: '100%',
            minHeight: '86.4vh',
            backgroundColor: '#f4f7f6',
            fontFamily: '"Ubuntu", sans-serif',
            fontSize: '14px',
            color: '#5A5A5A',
            marginBottom: '20px',
            paddingRight: { xs: '5px', sm: '15px' },
            paddingLeft: { xs: '5px', sm: '15px' },
            boxSizing: 'border-box',
        },
        container: {
            padding: { xs: '5px', sm: '0' },
        },
        tableCard: {
            backgroundColor: '#fff',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            padding: { xs: '10px', sm: '15px' },
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
        },
    };

    useEffect(() => {
        fetchDevices();
    }, []);

    const fetchDevices = async () => {
        try {
            setLoading(true);
            const slaves = await getCompressorSlaves();

            setDeviceObjects(slaves);
            const deviceNames = slaves.map(device => device.slave_name);
            setDevices(['all', ...deviceNames]);

            // Default to 'all' if available, otherwise first device
            if (slaves.length > 0) {
                setFilterDevice('all');
            }
        } catch (err) {
            console.error('Error fetching devices:', err);
            setError(err.message || 'Failed to fetch devices');
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch analytics data for a single device
    const fetchAnalyticsData = async (deviceName, parameters, startDate, endDate) => {
        const selectedDevice = deviceObjects.find(device => device.slave_name === deviceName);
        if (!selectedDevice) {
            throw new Error('Device not found');
        }

        const from = dayjs(startDate).format('YYYY-MM-DDTHH:mm:ss');
        const to = dayjs(endDate).format('YYYY-MM-DDTHH:mm:ss');

        const response = await getCompressorAnalytics(
            selectedDevice.slave_id,
            from,
            to
        );

        return response.data.data || [];
    };

    // Handle search button click
    const handleSearch = async () => {
        // Removed the check that blocked 'all'
        if (!filterStartDate || !filterEndDate) {
            setSnackbarMessage('Please select date range');
            setSnackbarOpen(true);
            return;
        }

        try {
            setDataLoading(true);
            setSearchClicked(true);

            // Logic to handle 'all' devices or single device
            let combinedData = [];

            if (filterDevice === 'all') {
                // Fetch all devices
                const promises = deviceObjects.map(async (device) => {
                    const data = await fetchAnalyticsData(device.slave_name, selectedParameter, filterStartDate, filterEndDate);
                    return data.map(item => ({ ...item, deviceName: device.slave_name })); // Add device name to data points
                });

                const results = await Promise.all(promises);
                combinedData = results.flat();
            } else {
                // Fetch single device
                const analyticsData = await fetchAnalyticsData(filterDevice, selectedParameter, filterStartDate, filterEndDate);
                combinedData = analyticsData.map(item => ({ ...item, deviceName: filterDevice }));
            }

            setFilteredChartData(combinedData);

            // Handle Comparison 1 (Fetch data regardless of merge status, needed for both cases)
            if (compareDevice) {
                setCompareLoading(true);
                try {
                    const compareData = await fetchAnalyticsData(compareDevice, selectedParameter2, filterStartDate, filterEndDate);
                    setCompareChartData(compareData);
                } catch (err) {
                    console.error('Error fetching comparison data:', err);
                } finally {
                    setCompareLoading(false);
                }
            }

            // Handle Comparison 2
            if (compareMode2 && compareDevice2) {
                setCompareLoading2(true);
                try {
                    const compareData2 = await fetchAnalyticsData(compareDevice2, selectedParameter3, filterStartDate, filterEndDate);
                    setCompareChartData2(compareData2);
                } catch (err) {
                    console.error('Error fetching second comparison data:', err);
                } finally {
                    setCompareLoading2(false);
                }
            }
        } catch (err) {
            console.error('Error fetching analytics data:', err);
            setSnackbarMessage(err.message || 'Failed to fetch analytics data');
            setSnackbarOpen(true);
        } finally {
            setDataLoading(false);
        }
    };

    const handleResetFilters = () => {
        setSearchTerm('');
        setFilterDevice(deviceObjects.length > 0 ? 'all' : '');
        setFilterStartDate(dayjs().subtract(1, 'day'));
        setFilterEndDate(dayjs());
        setSearchClicked(false);
        setSelectedParameter(['connectivity_status']);
        setSelectedParameter2(['connectivity_status']);
        setSelectedParameter3(['connectivity_status']);
        setFilterDevice2('all');
        setFilterDevice3('all');
        setFilteredChartData([]);
        setCompareChartData([]);
        setCompareChartData2([]);
        setError(null);
        setMergeComparison(false);
        setCompareMode(false);
        setCompareDevice('');
    };

    // State now holds data with deviceName property included
    const [filteredChartData, setFilteredChartData] = useState([]);
    const [compareMode, setCompareMode] = useState(false);
    const [compareDevice, setCompareDevice] = useState('');
    const [compareChartData, setCompareChartData] = useState([]);
    const [compareMode2, setCompareMode2] = useState(false);
    const [compareDevice2, setCompareDevice2] = useState('');
    const [compareChartData2, setCompareChartData2] = useState([]);

    const handleCompareDeviceChange = async (deviceName) => {
        setCompareDevice(deviceName);
        setCompareMode(true);

        if (searchClicked) {
            setCompareLoading(true);
            try {
                const compareData = await fetchAnalyticsData(deviceName, selectedParameter2, filterStartDate, filterEndDate);
                setCompareChartData(compareData);
            } catch (err) {
                console.error('Error fetching comparison data:', err);
            } finally {
                setCompareLoading(false);
            }
        }
    };

    const handleCompareDevice2Change = async (deviceName) => {
        setCompareDevice2(deviceName);
        setCompareMode2(true);

        if (searchClicked && filteredChartData.length > 0) {
            setCompareLoading2(true);
            try {
                const compareData2 = await fetchAnalyticsData(deviceName, selectedParameter3, filterStartDate, filterEndDate);
                setCompareChartData2(compareData2);
            } catch (err) {
                console.error('Error fetching second comparison data:', err);
            } finally {
                setCompareLoading2(false);
            }
        }
    };

    // Process Comparison Data (Single Device) - Helper function
    const processRawData = (rawData, deviceName) => {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            return [];
        }
        const seriesData = [];
        rawData.forEach(item => {
            const startTime = new Date(item.start).getTime();
            const endTime = new Date(item.end).getTime();
            const status = item.status;

            seriesData.push([startTime, status]);
            seriesData.push([endTime, status]);
        });
        seriesData.sort((a, b) => a[0] - b[0]);
        return seriesData;
    };

    // Process data for Main Chart (Supports Multiple Devices + Merged Comparison)
    const processedFilteredData = React.useMemo(() => {
        if (!filteredChartData || filteredChartData.length === 0) {
            return { series: [] };
        }

        // Group data by deviceName
        const groupedByDevice = filteredChartData.reduce((acc, item) => {
            const name = item.deviceName || 'Unknown';
            if (!acc[name]) acc[name] = [];
            acc[name].push(item);
            return acc;
        }, {});

        // Create series for each device in main selection
        const series = Object.keys(groupedByDevice).map(deviceName => {
            const devicePoints = groupedByDevice[deviceName];
            const seriesData = [];

            devicePoints.forEach(item => {
                const startTime = new Date(item.start).getTime();
                const endTime = new Date(item.end).getTime();
                const status = item.status;

                seriesData.push([startTime, status]);
                seriesData.push([endTime, status]);
            });

            seriesData.sort((a, b) => a[0] - b[0]);

            return {
                name: deviceName,
                data: seriesData
            };
        });

        // If Merge Comparison is checked, add compare device data to this series array
        if (mergeComparison && compareDevice && compareChartData.length > 0) {
            const mergedData = processRawData(compareChartData, compareDevice);
            // Avoid duplicate device names if somehow already selected (though UI prevents it)
            if (!series.find(s => s.name === compareDevice)) {
                series.push({
                    name: compareDevice,
                    data: mergedData
                });
            }
        }

        return { series };
    }, [filteredChartData, mergeComparison, compareDevice, compareChartData]);

    // Process Comparison Data (Separate Chart - used when mergeComparison is false)
    const processedCompareData = React.useMemo(() => {
        if (mergeComparison) return { series: [] }; // Don't process if merging to main chart

        if (!compareChartData || !Array.isArray(compareChartData) || compareChartData.length === 0) {
            return { series: [] };
        }

        const seriesData = processRawData(compareChartData, compareDevice);

        return {
            series: [{
                name: compareDevice || 'Connectivity',
                data: seriesData
            }]
        };
    }, [compareChartData, compareDevice, mergeComparison]);

    const processedCompareData2 = React.useMemo(() => {
        if (!compareChartData2 || !Array.isArray(compareChartData2) || compareChartData2.length === 0) {
            return { series: [] };
        }

        const seriesData = processRawData(compareChartData2, compareDevice2);

        return {
            series: [{
                name: compareDevice2 || 'Connectivity',
                data: seriesData
            }]
        };
    }, [compareChartData2, compareDevice2]);

    // Chart Options
    const getChartOptions = (isMultiDevice = false) => {
        // Basic color palette for multiple devices
        const colors = ['#30b44a', '#2F6FB0', '#e34d4d', '#f4b400', '#9c27b0', '#00bcd4'];

        return {
            chart: {
                type: 'area',
                height: 420,
                toolbar: { show: true },
                zoom: { enabled: true },
                background: '#FFFFFF',
            },
            stroke: {
                width: 2,
                curve: 'stepline'
            },
            fill: {
                type: 'solid',
                opacity: 0.2,
            },
            colors: colors,
            dataLabels: { enabled: false },
            xaxis: {
                type: 'datetime',
                title: {
                    text: 'Time',
                    style: { color: '#6B7280', fontSize: '12px' },
                },
                labels: {
                    style: { colors: '#6B7280', fontSize: '11px' },
                    datetimeUTC: false
                },
            },
            yaxis: {
                title: {
                    text: 'Status',
                    style: { color: '#6B7280', fontSize: '12px' },
                },
                min: -0.1,
                max: 1.1,
                tickAmount: 2,
                labels: {
                    style: { colors: '#6B7280', fontSize: '12px' },
                    formatter: function (val) {
                        if (val >= 0.9) return 'Online';
                        if (val <= 0.1) return 'Offline';
                        return '';
                    }
                },
            },
            grid: {
                borderColor: '#E5E7EB',
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: true } },
            },
            tooltip: {
                enabled: true,
                theme: 'light',
                shared: isMultiDevice, // Shared crosshair for multi-device
                // Unified Custom Tooltip
                custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                    const allSeries = w.globals.initialSeries;
                    let tooltipHtml = '';
                    let timestamp = null;

                    // Helper function to create the status row HTML
                    const getStatusRow = (name, value) => {
                        const statusText = value === 1 ? 'Online' : 'Offline';
                        const statusColor = value === 1 ? '#30b44a' : '#e34d4d';
                        return `
                            <div style="display: flex; align-items: center; margin-top: 4px;">
                                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${statusColor}; margin-right: 8px;"></span>
                                <span style="flex: 1; color: #333; font-size: 12px;">${name}:</span>
                                <span style="font-weight: bold; color: ${statusColor}; margin-left: 5px; font-size: 12px;">${statusText}</span>
                            </div>
                        `;
                    };

                    // Logic for Multi-Device (Show all devices in list)
                    if (isMultiDevice) {
                        // Iterate through all series
                        allSeries.forEach((s, i) => {
                            const point = s.data[dataPointIndex];
                            // Check if point exists to avoid errors
                            if (point) {
                                if (!timestamp) timestamp = point[0]; // Capture timestamp once
                                tooltipHtml += getStatusRow(s.name, point[1]);
                            }
                        });
                    }
                    // Logic for Single Device
                    else {
                        const point = allSeries[seriesIndex].data[dataPointIndex];
                        if (point) {
                            timestamp = point[0];
                            tooltipHtml += getStatusRow(allSeries[seriesIndex].name, point[1]);
                        }
                    }

                    // Format the timestamp in 12-hour format using dayjs
                    // 'DD/MM/YYYY hh:mm:ss A' -> 12 hour format with AM/PM
                    const formattedDate = timestamp ? dayjs(timestamp).format('DD/MM/YYYY hh:mm:ss A') : 'N/A';

                    return `
                        <div style="padding: 10px; background-color: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
                            <div style="font-weight: bold; margin-bottom: 8px; color: #333; font-size: 12px;">${formattedDate}</div>
                            ${tooltipHtml}
                        </div>
                    `;
                }
            },
            legend: {
                show: isMultiDevice, // Show legend only if multiple devices
                position: 'top',
                horizontalAlign: 'center'
            },
            markers: {
                size: 0,
                hover: { size: 5 }
            }
        };
    };

    return (
        <Box sx={styles.mainContent} id="main-content">
            <Box sx={styles.container}>
                <Card sx={styles.tableCard}>
                    <CardContent sx={{ p: { xs: 1, sm: 1 } }}>
                        <Box className="logs-header">
                            <Box
                                className="logs-filters"
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    flexWrap: 'wrap',
                                    gap: { xs: 2, sm: 2 },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                }}
                            >
                                {loading ? (
                                    <Box style={styles.loadingContainer}>
                                        <CircularProgress />
                                    </Box>
                                ) : error ? (
                                    <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
                                ) : (
                                    <>
                                        {/* Machine Select */}
                                        <FormControl
                                            size="small"
                                            sx={{
                                                minWidth: { xs: '100%', sm: 300 },
                                                order: { xs: 1, sm: 1 }
                                            }}
                                        >
                                            <InputLabel>Select Device</InputLabel>
                                            <Select
                                                value={filterDevice}
                                                label="Select Device"
                                                onChange={(e) => setFilterDevice(e.target.value)}
                                            >
                                                {devices.map((device) => (
                                                    <MenuItem key={device} value={device}>
                                                        {device === 'all' ? 'All Devices' : device}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Parameters Select */}
                                        <FormControl
                                            size="small"
                                            sx={{
                                                minWidth: { xs: '100%', sm: 200 },
                                                mr: { sm: 1 },
                                                order: { xs: 2, sm: 2 }
                                            }}
                                        >
                                            <InputLabel>Select Parameters</InputLabel>
                                            <Select
                                                multiple
                                                value={selectedParameter}
                                                onChange={(e) => setSelectedParameter(e.target.value)}
                                                label="Select Parameters"
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Chip key="conn" label="Connectivity History" size="small" />
                                                    </Box>
                                                )}
                                            >
                                                {parameterOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        <Checkbox checked={selectedParameter.indexOf(option.value) > -1} />
                                                        <ListItemText primary={option.label} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Date Pickers Row */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                gap: { xs: 2, sm: 2 },
                                                order: { xs: 3, sm: 3 },
                                                width: { xs: '100%', sm: 'auto' }
                                            }}
                                        >
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DateTimePicker
                                                    open={openStart}
                                                    onOpen={() => setOpenStart(true)}
                                                    onClose={() => setOpenStart(false)}
                                                    value={filterStartDate}
                                                    onChange={(newValue) => setFilterStartDate(newValue)}
                                                    format="DD/MM/YYYY hh:mm A"
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            sx: {
                                                                minWidth: { xs: '100%', sm: 220 },
                                                                mr: { sm: 2 },
                                                                borderRadius: 2
                                                            },
                                                            onClick: () => setOpenStart(true),
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>

                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DateTimePicker
                                                    open={openEnd}
                                                    onOpen={() => setOpenEnd(true)}
                                                    onClose={() => setOpenEnd(false)}
                                                    value={filterEndDate}
                                                    onChange={(newValue) => setFilterEndDate(newValue)}
                                                    format="DD/MM/YYYY hh:mm A"
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            sx: {
                                                                minWidth: { xs: '100%', sm: 220 },
                                                                mr: { sm: 2 },
                                                                borderRadius: 2
                                                            },
                                                            onClick: () => setOpenEnd(true),
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Box>

                                        {/* Buttons Row */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                gap: 1,
                                                order: { xs: 4, sm: 4 },
                                                justifyContent: { xs: 'flex-start', sm: 'flex-start' }
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={<SearchIcon />}
                                                onClick={handleSearch}
                                                sx={{
                                                    backgroundColor: '#2F6FB0',
                                                    '&:hover': { backgroundColor: '#1E4A7C' },
                                                    minWidth: 'auto',
                                                    height: '32px',
                                                    padding: { xs: '6px 16px', sm: '6px' },
                                                    borderRadius: '4px',
                                                }}
                                            >
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                startIcon={<RefreshIcon />}
                                                onClick={handleResetFilters}
                                                sx={{
                                                    borderColor: '#6c757d',
                                                    color: '#6c757d',
                                                    '&:hover': { borderColor: '#5a6268', color: '#5a6268' },
                                                    minWidth: 'auto',
                                                    height: '32px',
                                                    padding: { xs: '6px 16px', sm: '4px' },
                                                    borderRadius: '4px',
                                                }}
                                            >
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* Chart Display Area */}
                        {searchClicked ? (
                            dataLoading ? (
                                <Box style={styles.loadingContainer}>
                                    <CircularProgress />
                                </Box>
                            ) : processedFilteredData.series.length > 0 ? (
                                <>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'flex-start', md: 'center' },
                                        mb: 1, mt: 2,
                                        gap: { xs: 1, md: 0 }
                                    }}>
                                        <Typography
                                            gutterBottom
                                            sx={{
                                                fontSize: { xs: '12px', sm: '14px' },
                                                fontWeight: 'bold',
                                                color: '#50342c',
                                                mb: 0
                                            }}
                                        >
                                            {filterDevice === 'all' ? 'All Devices' : filterDevice} - Connectivity History
                                            {mergeComparison && compareDevice && ` compared with ${compareDevice}`}
                                        </Typography>
                                        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                                                {/* Compare Logic - Only relevant if a specific device is selected in main, or comparing against specific */}
                                                {filterDevice !== 'all' && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {/* NEW: Checkbox for merging charts */}
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={mergeComparison}
                                                                    onChange={(e) => setMergeComparison(e.target.checked)}
                                                                    disabled={!compareDevice}
                                                                    size="small"
                                                                />
                                                            }
                                                            label="Merge Charts"
                                                            sx={{ mr: 0 }}
                                                        />
                                                        
                                                        {compareMode ? (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setCompareMode(false);
                                                                    setMergeComparison(false);
                                                                    setCompareDevice('');
                                                                }}
                                                                sx={{ borderColor: '#d32f2f', color: '#d32f2f', width: { xs: '100%', sm: 'auto' } }}
                                                            >
                                                                Cancel Compare
                                                            </Button>
                                                        ) : (
                                                            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 300 }, width: { xs: '100%', md: 'auto' } }}>
                                                                <InputLabel>Select Device to Compare</InputLabel>
                                                                <Select
                                                                    value={compareDevice}
                                                                    label="Select Device to Compare"
                                                                    onChange={(e) => handleCompareDeviceChange(e.target.value)}
                                                                >
                                                                    {devices.filter(device => device !== 'all' && device !== filterDevice).map((device) => (
                                                                        <MenuItem key={device} value={device}>{device}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ width: '100%', overflow: 'auto' }}>
                                        <Chart
                                            // Pass true if multiple series exist (All devices or merged comparison)
                                            options={getChartOptions(processedFilteredData.series.length > 1)}
                                            series={processedFilteredData.series}
                                            type="area"
                                            height={420}
                                            width="100%"
                                        />
                                    </Box>

                                    {/* Comparison Charts - Only render if NOT merged into main chart */}
                                    {!mergeComparison && compareMode && compareDevice && (
                                        <Box sx={{ mt: 4 }}>
                                            <Typography gutterBottom sx={{ fontSize: '14px', fontWeight: 'bold', color: '#50342c', mb: 1 }}>
                                                {compareDevice} - Connectivity History
                                            </Typography>
                                            {compareLoading ? (
                                                <CircularProgress />
                                            ) : processedCompareData.series.length > 0 ? (
                                                <Box sx={{ width: '100%', overflow: 'auto' }}>
                                                    <Chart
                                                        options={getChartOptions(false)}
                                                        series={processedCompareData.series}
                                                        type="area"
                                                        height={420}
                                                        width="100%"
                                                    />
                                                </Box>
                                            ) : (
                                                <Alert severity="info">No data available for comparison</Alert>
                                            )}
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Alert severity="info" sx={{ m: 2 }}>No data available for the selected date range</Alert>
                            )
                        ) : null}
                    </CardContent>
                </Card>
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Box>
    )
}

export default CompressorAnalytics;